import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

// Handles keyboard and mouse navigation for suggestion lists from the composer
// Probably could be applied to the various other context menus too

const clampIndex = (index, itemCount) => Math.min(Math.max(index, 0), itemCount - 1);

export function reconcileSelectedIndex({
  items,
  currentIndex,
  previousItems,
  previousSelectedIndex,
  getItemKey,
  missingSelectionFallbackIndex,
  initialIndex,
  resetSelection,
}) {
  if (items.length === 0) return -1;
  if (resetSelection && initialIndex !== undefined) return clampIndex(initialIndex, items.length);

  const hasKeyedReconciliation = !!getItemKey && missingSelectionFallbackIndex !== undefined;
  if (hasKeyedReconciliation) {
    if (previousItems !== items && previousSelectedIndex >= 0 && previousSelectedIndex < previousItems.length) {
      const previousSelectedKey = getItemKey(previousItems[previousSelectedIndex]);
      const nextSelectedIndex = items.findIndex((item) => getItemKey(item) === previousSelectedKey);

      if (nextSelectedIndex >= 0) return nextSelectedIndex;
      return clampIndex(missingSelectionFallbackIndex, items.length);
    }

    if (currentIndex < 0 && initialIndex !== undefined) return clampIndex(initialIndex, items.length);
    return Math.max(-1, Math.min(currentIndex, items.length - 1));
  }

  if (initialIndex !== undefined) return clampIndex(initialIndex, items.length);
  return Math.max(-1, Math.min(currentIndex, items.length - 1));
}

export function useListNavigation({
  items,
  onSelect,
  ref,
  wrap = false,
  additionalSelectKeys = [],
  initialIndex,
  getItemKey,
  missingSelectionFallbackIndex,
  selectionResetKey = undefined,
}) {
  const [selectedIndex, setSelectedIndex] = useState(initialIndex ?? -1);
  // Lets consumers distinguish keyboard nav from mouse hover, e.g. to only scroll on keyboard
  const isKeyboardNavRef = useRef(false);

  const selectItem = useCallback(
    (index) => {
      if (index < 0 || index >= items.length) return;
      onSelect(items[index]);
    },
    [items, onSelect],
  );

  // When nothing is selected, up jumps to the last item and down jumps to the first.
  const upHandler = useCallback(() => {
    if (items.length === 0) return;
    setSelectedIndex((prev) => {
      let next;
      if (prev < 0) {
        next = items.length - 1;
      } else if (wrap) {
        next = prev <= 0 ? items.length - 1 : prev - 1;
      } else {
        next = Math.max(0, prev - 1);
      }
      if (next !== prev) isKeyboardNavRef.current = true;
      return next;
    });
  }, [items.length, wrap]);

  const downHandler = useCallback(() => {
    if (items.length === 0) return;
    setSelectedIndex((prev) => {
      let next;
      if (prev < 0) {
        next = 0;
      } else if (wrap) {
        next = prev >= items.length - 1 ? 0 : prev + 1;
      } else {
        next = Math.min(items.length - 1, prev + 1);
      }
      if (next !== prev) isKeyboardNavRef.current = true;
      return next;
    });
  }, [items.length, wrap]);

  const enterHandler = useCallback(() => {
    selectItem(selectedIndex);
  }, [selectItem, selectedIndex]);

  const handleMouseMove = useCallback((index) => {
    isKeyboardNavRef.current = false;
    setSelectedIndex(index);
  }, []);

  // Use with onMouseMove so hover updates after keyboard navigation.
  const createMouseEnterHandler = useCallback(
    (index) => {
      return () => handleMouseMove(index);
    },
    [handleMouseMove],
  );

  const createClickHandler = useCallback(
    (index) => {
      return (e) => {
        e.preventDefault();
        selectItem(index);
      };
    },
    [selectItem],
  );

  const onMouseLeave = useCallback(() => {
    setSelectedIndex(-1);
  }, []);

  // Keep latest selection available to reconciliation without re-running that effect on every arrow key.
  const selectedIndexRef = useRef(selectedIndex);
  // Snapshot the previous items array so we can tell when a new result set arrives.
  const previousItemsRef = useRef(items);
  const previousSelectionResetKeyRef = useRef(selectionResetKey);

  useEffect(() => {
    selectedIndexRef.current = selectedIndex;
  }, [selectedIndex]);

  // Reconcile selection when items change. For keyed flows, track the previously
  // selected item by key and follow it to its new position (or fall back).
  useEffect(() => {
    const previousItems = previousItemsRef.current;
    const previousSelectedIndex = selectedIndexRef.current;
    const itemsChanged = previousItems !== items;
    // Wait for the new query's result array before resetting. Async lists can
    // re-render with the old items first, then publish the filtered results.
    const resetSelection = selectionResetKey !== undefined && previousSelectionResetKeyRef.current !== selectionResetKey && itemsChanged;

    setSelectedIndex((currentIndex) =>
      reconcileSelectedIndex({
        items,
        currentIndex,
        previousItems,
        previousSelectedIndex,
        getItemKey,
        missingSelectionFallbackIndex,
        initialIndex,
        resetSelection,
      }),
    );

    if (resetSelection) previousSelectionResetKeyRef.current = selectionResetKey;
    previousItemsRef.current = items;
  }, [getItemKey, initialIndex, items, missingSelectionFallbackIndex, selectionResetKey]);

  const onKeyDown = useCallback(
    ({ event }) => {
      if (event.key === 'ArrowUp') {
        upHandler();
        return true;
      }

      if (event.key === 'ArrowDown') {
        downHandler();
        return true;
      }

      if (event.key === 'Enter' || event.key === ' ' || additionalSelectKeys.includes(event.key)) {
        enterHandler();
        return true;
      }

      return false;
    },
    [additionalSelectKeys, downHandler, enterHandler, upHandler],
  );

  useImperativeHandle(ref, () => ({ onKeyDown }), [onKeyDown]);

  return {
    selectedIndex,
    setSelectedIndex,
    isKeyboardNavRef,
    createMouseEnterHandler,
    createClickHandler,
    onMouseLeave,
    onKeyDown,
  };
}
